import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useAppNavigate from "../../hooks/useAppNavigate";
import { income, expense, transfer } from "../../common/imagePath";
import { GreenCheckIcon } from "../../components/ui/icons";
import AppNavMockup from "../../components/widgets/AppNavMockup";
import StepRow from "../../components/widgets/StepRow";
import HelpfulCard from "../../components/widgets/HelpfulCard";
import PageHeader from "../../components/widgets/PageHeader";
import HelpfulTipsCard from "../../components/widgets/HelpfulTipsCard";

const TRANSACTION_TYPES = [
  { id: "expense", color: "#EB4D3C", img: expense },
  { id: "income", color: "#16A23F", img: income },
  { id: "transfer", color: "#3479F7", img: transfer },
];

const ORDER = ["income", "expense", "transfer"];

const TransactionTabBar = ({ activeType, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-center px-4 pb-3 bg-[#F2F2F7] dark:bg-[#000000]">
      <div className="flex items-center h-10 rounded-full p-1 bg-white dark:bg-[#1C1C1E] transition-colors duration-200 w-165 mt-3">
        {ORDER.map((typeId) => {
          const type = TRANSACTION_TYPES.find((t) => t.id === typeId);
          const isActive = activeType === typeId;

          if (isActive) {
            return (
              <button
                key={typeId}
                onClick={() => onChange(typeId)}
                className="flex items-center flex-shrink-0 w-[82px] h-[24px] pl-1 pr-2 transition-all duration-200 active:opacity-70 rounded-full bg-[#F3F4F6] dark:bg-[#2C2E30]"
              >
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0">
                  <img
                    src={type.img}
                    alt=""
                    className="w-4 h-4 object-contain"
                  />
                </div>
                <div className="flex items-center justify-center flex-1 h-full">
                  <span
                    className="text-xs font-medium leading-none whitespace-nowrap"
                    style={{ color: type.color }}
                  >
                    {t(`addTransactions.tabs.${typeId}`)}
                  </span>
                </div>
              </button>
            );
          }

          return (
            <button
              key={typeId}
              onClick={() => onChange(typeId)}
              className="flex items-center justify-center w-8 h-8 flex-shrink-0 transition-all duration-200 active:opacity-70"
            >
              <img
                src={type.img}
                alt={t(`addTransactions.tabs.${typeId}`)}
                className="w-4 h-4 object-contain"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AddTransactions = () => {
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const [activeType, setActiveType] = useState("expense");
  const [animating, setAnimating] = useState(false);

  const handleTypeChange = (id) => {
    if (id === activeType || animating) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveType(id);
      setAnimating(false);
    }, 180);
  };

  const steps = t(`addTransactions.steps.${activeType}`, {
    returnObjects: true,
  });
  const tips = t("addTransactions.tips", { returnObjects: true });

  return (
    <div className="bg-[#F2F2F7] dark:bg-[#000000] transition-colors duration-200 pb-4">
      {/* Header */}
      <PageHeader
        title={t("addTransactions.title")}
        onBack={() => navigate("/how-to-use")}
      />

      {/* Tab bar */}
      <TransactionTabBar activeType={activeType} onChange={handleTypeChange} />

      {/* Content — fades when switching type */}
      <div
        style={{ opacity: animating ? 0 : 1, transition: "opacity 0.18s ease" }}
      >
        {/* Card 1 — Overview */}
        <div className="mx-4 mt-3 mb-4 rounded-2xl overflow-hidden  border border-[#DEDEDE] dark:border-black dark:bg-[#1C1C1E]">
          <div className="px-4 pt-4 pb-3 ">
            <h2 className="font-20px font-bold text-[#1C1C1E] dark:text-white mb-1">
              {t("addTransactions.cardTitle")}
            </h2>
            <p className="text-xs font-normal font-inter text-[#636363] dark:text-[#EBEBF5] dark:opacity-60 leading-relaxed mb-3">
              {t("addTransactions.cardDesc")}
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <img src={expense} alt="" className=" h-4 object-contain " />
                <span className="text-xs font-semibold text-[#1C1C1E] dark:text-white">
                  {t("addTransactions.expenseLabel")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <img src={income} alt="" className="h-4 object-contain" />
                <span className="text-xs font-semibold text-[#1C1C1E] dark:text-white">
                  {t("addTransactions.incomeLabel")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <img src={transfer} alt="" className=" h-4 object-contain" />
                <span className="text-xs font-semibold text-[#1C1C1E] dark:text-white">
                  {t("addTransactions.transferLabel")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2 — App mockup */}
        <div className="-mb-3">
          <AppNavMockup activeRoute="/how-to-add-transactions" />
        </div>

        {/* Section label */}
        <p className="px-4 mt-3 mb-2 text-xs font-semibold tracking-wide text-[#8E8E93] uppercase">
          {t(`addTransactions.sectionLabel.${activeType}`)}
        </p>

        {/* Card 3 — Steps */}
        <div className="mx-4 mb-4  rounded-2xl overflow-hidden bg-white dark:bg-[#1C1C1E]">
          <div className="px-4 ">
            {steps.map((step, i) => (
              <StepRow
                key={i}
                number={i + 1}
                title={step.title}
                desc={step.desc}
                isLast={i === steps.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Card 4 — Helpful Tips */}
        <HelpfulTipsCard
          tips={tips}
          BulletComponent={GreenCheckIcon}
          itemAlign="items-start"
        />

        {/* Card 5 — Was this helpful? */}
        <HelpfulCard />
      </div>
    </div>
  );
};

export default AddTransactions;
